import React from 'react';
import { CheckCircleOutline, LockClock, PlayArrow } from '@mui/icons-material';
import { IconButton, Typography, Box, Divider, LinearProgress } from '@mui/material';
import { useAuthFeatures } from '../../hooks/use-auth-features';

interface ModalAccordionListProps {
  title?: string;
  duration: number;
  status?: 'completed' | 'in_progress' | 'not_started' | string;
  isLastItem?: boolean;
  progress?: number;
  totalDuration?: number;
  isFirstIncomplete?: boolean;
  onLessonSelect?: (lessonUrl: string) => void;
  lessonUrl?: string;
}

export const ModalAccordionList: React.FC<ModalAccordionListProps> = ({
  title,
  duration,
  status,
  isLastItem,
  progress = 0,
  totalDuration = 1,
  isFirstIncomplete,
  onLessonSelect,
  lessonUrl,
}) => {
  const { isLoggedIn, isSubscribedUser } = useAuthFeatures();
  // Calculate progress percentage
  const progressPercentage =
    totalDuration > 0 ? Math.min(100, (progress / totalDuration) * 100) : 0;

  const handleClick = () => {
    if (isFirstIncomplete && onLessonSelect && lessonUrl) {
      onLessonSelect(lessonUrl);
    }
  };

  const renderStatus = () => {
    switch (status) {
      case 'completed':
        return (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            className="text-text-secondary"
          >
            <CheckCircleOutline sx={{ width: '16px', height: '16px' }} />
            <Typography variant="caption-small">Completato</Typography>
          </Box>
        );
      case 'in_progress':
        return (
          <>
            {progressPercentage > 0 && isLoggedIn && !isFirstIncomplete && isSubscribedUser && (
              <div className="w-[61px] flex flex-col items-center justify-center">
                <PlayArrow sx={{ width: '16px', height: '16px' }} />
                <Typography variant="caption-small">{progressPercentage.toFixed(0)}%</Typography>
              </div>
            )}
            {isLoggedIn && isFirstIncomplete && isSubscribedUser ? (
              <div className="w-[61px] flex flex-col items-center justify-center">
                <IconButton color="primary" onClick={handleClick} className="">
                  <PlayArrow />
                </IconButton>
                <Typography variant="caption-small">{progressPercentage.toFixed(0)}%</Typography>
              </div>
            ) : null}
          </>
        );
      case 'not_started':
        return (
          <>
            {isSubscribedUser && isLoggedIn && isFirstIncomplete ? (
              <div className="flex justify-center w-[61px]">
                <IconButton color="primary" onClick={handleClick}>
                  <PlayArrow />
                </IconButton>
              </div>
            ) : null}
          </>
        );
      default:
        return (
          <>
            {!isSubscribedUser && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                className="text-text-secondary"
              >
                <LockClock sx={{ width: '16px', height: '16px' }} />
                <Typography variant="caption-small">Abbonati per accedere</Typography>
              </Box>
            )}
          </>
        );
    }
  };
  return (
    <div className="px-2">
      <Box display="flex" alignItems="center" gap={2} className="py-2">
        <Box flexGrow={1}>
          <Typography
            variant="s2"
            component="h4"
            color={status === 'completed' ? 'textSecondary' : 'textPrimary'}
          >
            {title}
          </Typography>
          <Typography variant="caption" component="p" color="textSecondary">
            {Math.round(duration / 60)} min
          </Typography>

          {/* Show progress bar for in_progress lessons */}
          {status === 'in_progress' && progressPercentage > 0 && isSubscribedUser && (
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              className="mt-1"
              sx={{ height: 4, borderRadius: 2 }}
            />
          )}
        </Box>

        {renderStatus()}
      </Box>
      {!isLastItem && <Divider className="my-2" />}
    </div>
  );
};
